export type ListingTransitionPart = 'photo' | 'meta';

/**
 * Return a deterministic, CSS-safe identity for a listing transition boundary.
 *
 * The MLS id is the only identity shared by the search payload, detail route,
 * and server-rendered pages. Missing or punctuation-only ids deliberately do
 * not receive a fallback name: a shared fallback would make unrelated listings
 * compete for the same native transition boundary.
 */
export function listingViewTransitionName(
  id: string | null | undefined,
  part: ListingTransitionPart
): string | undefined {
  const source = typeof id === 'string' ? id.trim() : '';
  if (!source) return undefined;

  const readable = source
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  if (!/[a-zA-Z0-9]/.test(readable)) return undefined;

  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `gr-listing-${readable}-${(hash >>> 0).toString(36)}-${part}`;
}

/**
 * Claim the one named occurrence allowed for a listing in an active view.
 *
 * Rails can be assembled from independent requests, so the same MLS listing
 * may appear more than once. Repeated cards remain visible, but only the first
 * copy receives a native transition boundary.
 */
export function claimListingViewTransition(
  mlsListingId: string | null | undefined,
  seen: Set<string>
): boolean {
  const key = typeof mlsListingId === 'string' ? mlsListingId.trim().toLowerCase() : '';
  if (!key || !listingViewTransitionName(mlsListingId, 'photo')) return false;
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
}
